import { useEffect, useState } from 'react'
import { ApiError } from '../api/client'
import type { XdrMode } from '../api/devices'
import {
  loadMultiModules,
  saveMultiModule,
  saveMultiModuleMenu,
  saveMultiProductivitee,
  saveMultiXdr,
  type MultiModule,
} from '../api/overviewMultiActions'
import {
  Info,
  LEVEL_HELP,
  PRODUCTIVITEE_LEVEL,
  PRODUCTIVITEE_LEVEL_HELP,
  ProductiviteeConsentDialog,
  XdrModeDialog,
} from '../devices/DeviceAgentModules'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import '../devices/DeviceAgentModules.css'
import './MultiModulesTab.css'

/**
 * Fila Modules din panoul de selectie multipla (`overview/multiple_modules`).
 *
 * TABELUL NOU, 19 octombrie 2026
 * -----------------------------------------------------------------------------
 * Pana aici fila desena inca tabelul vechi - Free / Premium / Application Menu /
 * Module extensions, cu "Show Application Icon" si "Remote Control" lipite sus
 * si cu surorile Softsee ascunse inca in lista - desi fisa aparatului trecuse
 * pe 24 septembrie 2026 la tabelul nou (`devices/DeviceAgentModules.tsx`). Acum
 * e acelasi tabel, cu aceleasi bucati: capetele, baloanele (`LEVEL_HELP`),
 * foaia `DeviceAgentModules.css`, fereastra de acord Productivitee si fereastra
 * XDR sunt cele de acolo, nu copii.
 *
 * - **Basic** in loc de Free + Premium: aprinderea scrie 'premium', stingerea
 *   'disabled' - acelasi contract ca pe fisa si ca inainte aici.
 * - **Module extensions**: Extended monitoring (Proactivitee), Extended
 *   (nivelul Productivitee, cu acord) si XDR (Securitee, cu fereastra lui).
 * - Productivitee se aprinde numai prin fereastra de acord, ca pe fisa - pana
 *   aici bifa Premium il aprindea pe toata selectia FARA acord.
 * - Remote Control pe ultimul rand; surorile Softsee (`Shell::HIDDEN_MODULES`)
 *   nu le mai intoarce serverul.
 *
 * CE RAMANE ALTFEL DECAT PE FISA, SI DE CE: casutele pornesc TOATE nebifate.
 * Nu se citeste starea niciunei masini bifate - douazeci de masini pot avea
 * douazeci de stari, iar o casuta n-are decat doua. Bifa data aici o SCRIE pe
 * toate. Din acelasi motiv:
 *
 * - Application Menu se deschide dupa ce s-a bifat Basic aici, ca pe fisa
 *   ("only while the module is on"), si se inchide la loc cand se scoate.
 * - "Show application icon" sta deasupra tabelului: pe fisa e in fila Settings,
 *   dar panoul de selectie n-are alt loc din care sa-l scrie pe toate.
 */
export function MultiModulesTab({ ids }: { ids: number[] }) {
  const [catalog, setCatalog] = useState<MultiModule[] | null>(null)
  const [canEditXdr, setCanEditXdr] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [appIcon, setAppIcon] = useState(false)
  const [remote, setRemote] = useState(false)
  const [on, setOn] = useState<Record<number, boolean>>({})
  const [menu, setMenu] = useState<Record<number, boolean>>({})
  const [extended, setExtended] = useState<Record<number, boolean>>({})
  const [level, setLevel] = useState<Record<number, number>>({})
  const [xdr, setXdr] = useState<XdrMode | null>(null)
  const [consent, setConsent] = useState<{ module: number; level: number } | null>(null)
  const [xdrOpen, setXdrOpen] = useState(false)

  useEffect(() => {
    let gone = false
    void loadMultiModules()
      .then((page) => {
        if (!gone) {
          setCatalog(page.catalog)
          setCanEditXdr(page.can_edit_xdr === true)
        }
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : 'Error')
      })
    return () => {
      gone = true
    }
  }, [])

  if (error) return <div className="alert alert-danger">{error}</div>
  if (catalog === null) return <div className="loading">Loading...</div>

  const saved = () => toast('Module saved!', 'Success', 'success')
  const failed = (err: unknown) =>
    toast(err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong. Please try again.', 'Error', 'error')
  const write = (module: number, status: string) => {
    void saveMultiModule(ids, module, status).then(saved).catch(failed)
  }
  const writeMenu = (module: number, status: string) => {
    void saveMultiModuleMenu(ids, module, status).then(saved).catch(failed)
  }

  const onAppIcon = (next: boolean) => {
    setAppIcon(next)
    writeMenu(0, next ? 'enabled_application_icon' : 'disabled_application_icon')
  }

  const onRemote = (next: boolean) => {
    setRemote(next)
    write(0, next ? 'enabled_remote_control' : 'disabled_remote_control')
  }

  /* Basic: 'premium' / 'disabled', ca pe fisa. Stingerea scoate si meniul -
     serverul il scoate oricum (`AgentSettings::saveModule`). */
  const onBasic = (id: number, next: boolean) => {
    setOn((s) => ({ ...s, [id]: next }))
    if (!next) setMenu((s) => ({ ...s, [id]: false }))
    write(id, next ? 'premium' : 'disabled')
  }

  const onMenu = (id: number, next: boolean) => {
    setMenu((s) => ({ ...s, [id]: next }))
    writeMenu(id, next ? 'enabled_menu' : 'disabled_menu')
  }

  const onExtendedMonitoring = (id: number, next: boolean) => {
    setExtended((s) => ({ ...s, [id]: next }))
    write(id, next ? 'enabled_extended' : 'disabled_extended')
  }

  /* Productivitee, ca `DeviceAgentModules`: aprinderea deschide fereastra de
     acord pe Basic, Extended o deschide pe Extended; stingerea si coborarea la
     Basic merg direct pe capul lui, fara fereastra. */
  const productivitee = (id: number, next: boolean) => {
    if (next) {
      setConsent({ module: id, level: PRODUCTIVITEE_LEVEL.basic })
      return
    }
    void saveMultiProductivitee(ids, { module: id, status: 'disabled', level: PRODUCTIVITEE_LEVEL.basic, consent: false, consentExtended: false })
      .then(() => {
        setOn((s) => ({ ...s, [id]: false }))
        setMenu((s) => ({ ...s, [id]: false }))
        setLevel((s) => ({ ...s, [id]: PRODUCTIVITEE_LEVEL.basic }))
        saved()
      })
      .catch(failed)
  }

  const productiviteeLevel = (id: number, toExtended: boolean) => {
    if (toExtended) {
      setConsent({ module: id, level: PRODUCTIVITEE_LEVEL.extended })
      return
    }
    void saveMultiProductivitee(ids, { module: id, status: 'premium', level: PRODUCTIVITEE_LEVEL.basic, consent: true, consentExtended: false })
      .then(() => {
        setLevel((s) => ({ ...s, [id]: PRODUCTIVITEE_LEVEL.basic }))
        saved()
      })
      .catch(failed)
  }

  return (
    <div className="mm-card">
      <div className="mm-appicon">
        <SettingsCheck
          name="application_icon"
          className="iCheckM iCheckAppIcon"
          value="1"
          checked={appIcon}
          onChange={onAppIcon}
          label="Show application icon"
        />
        <Info what="Show application icon" text={APP_ICON_HELP} />
      </div>
      <table className="dam-tbl">
        <thead>
          <tr>
            <th className="a-left dam-c-name">Module</th>
            <th className="dam-c-agent">
              Basic
              <Info what="Basic" text={LEVEL_HELP.agent} />
            </th>
            <th className="dam-c-menu">
              Application Menu
              <Info what="Application Menu" text={LEVEL_HELP.menu} />
            </th>
            <th className="dam-c-ext">
              Module extensions
              <Info what="Module extensions" text={LEVEL_HELP.extensions} />
            </th>
          </tr>
        </thead>
        <tbody>
          {catalog.map((module) => {
            const isOn = on[module.id] ?? false
            const isProductivitee = module.productivitee === true
            return (
              <tr key={module.id}>
                <td scope="row" className="a-left dam-name">
                  {module.name}
                </td>
                <td className="dam-c-agent">
                  <SettingsCheck
                    aria-label={`${module.name} - ${isProductivitee ? 'enabled' : 'agent'}`}
                    name={`premium_module_${module.id}`}
                    data-module={module.id}
                    className="iCheckModulePremium iCheckM"
                    value={module.id}
                    checked={isOn}
                    onChange={(next) => (isProductivitee ? productivitee(module.id, next) : onBasic(module.id, next))}
                  />
                </td>
                <td className="dam-c-menu">
                  <SettingsCheck
                    aria-label={`${module.name} - menu`}
                    name={`menu_module_${module.id}`}
                    data-module={module.id}
                    className="iCheckModuleMenu iCheckM"
                    value={module.id}
                    checked={menu[module.id] ?? false}
                    disabled={!isOn}
                    onChange={(next) => onMenu(module.id, next)}
                  />
                </td>
                <td className="dam-c-ext">
                  {module.extended ? (
                    <>
                      <SettingsCheck
                        name="extended_monitoring"
                        data-module={module.id}
                        className="iCheckModuleExtended"
                        value="1"
                        checked={extended[module.id] ?? false}
                        onChange={(next) => onExtendedMonitoring(module.id, next)}
                        label="Extended monitoring"
                      />
                      <Info what="Extended monitoring" text={LEVEL_HELP.extendedMonitoring} />
                    </>
                  ) : null}
                  {isProductivitee && isOn ? (
                    <>
                      <SettingsCheck
                        aria-label={`${module.name} - extended`}
                        name={`productivitee_extended_${module.id}`}
                        data-module={module.id}
                        className="iCheckProductiviteeExtended"
                        value="1"
                        checked={(level[module.id] ?? PRODUCTIVITEE_LEVEL.basic) === PRODUCTIVITEE_LEVEL.extended}
                        onChange={(next) => productiviteeLevel(module.id, next)}
                        label="Extended"
                      />
                      <Info what="Extended" text={PRODUCTIVITEE_LEVEL_HELP.extended} />
                    </>
                  ) : null}
                  {module.xdr ? (
                    <>
                      {/* clicul deschide fereastra, nu scrie - ca pe fisa */}
                      <SettingsCheck
                        name="xdr"
                        data-module={module.id}
                        className="iCheckModuleXdr"
                        value="1"
                        checked={xdr === 'on'}
                        disabled={!canEditXdr}
                        title={canEditXdr ? undefined : 'You need the right to edit XDR settings.'}
                        onChange={() => setXdrOpen(true)}
                        label="XDR"
                      />
                      <Info what="XDR" text={LEVEL_HELP.xdr} />
                      {xdr !== null ? (
                        <span className="font-grey-cascade xdr-state" style={{ marginLeft: 6 }}>
                          {XDR_SHORT[xdr]}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </td>
              </tr>
            )
          })}
          <tr className="dam-rc">
            <td className="a-left dam-name">Remote Control</td>
            <td className="dam-rc-cell" colSpan={3}>
              <SettingsCheck
                name="remote_control"
                className="iCheckM iCheckRemoteControl"
                value="1"
                checked={remote}
                onChange={onRemote}
                label={<span className="dam-rc-note">not a module - on/off only</span>}
              />
              <Info what="Remote Control" text={LEVEL_HELP.remoteControl} />
            </td>
          </tr>
        </tbody>
      </table>
      {xdrOpen ? (
        <XdrModeDialog
          current={null}
          follow={null}
          title={`XDR on ${ids.length === 1 ? 'the selected device' : `the ${ids.length} selected devices`}`}
          onClose={() => setXdrOpen(false)}
          onSave={async (mode) => {
            try {
              await saveMultiXdr(ids, mode)
              setXdr(mode)
              saved()
              setXdrOpen(false)
            } catch (err) {
              failed(err)
            }
          }}
        />
      ) : null}
      {consent !== null ? (
        <ProductiviteeConsentDialog
          initialLevel={consent.level}
          intro="Choose how much the selected devices record:"
          onClose={() => setConsent(null)}
          onSave={async (choice) => {
            await saveMultiProductivitee(ids, { module: consent.module, status: 'premium', ...choice })
            setOn((s) => ({ ...s, [consent.module]: true }))
            setLevel((s) => ({ ...s, [consent.module]: choice.level }))
            setConsent(null)
            saved()
          }}
        />
      ) : null}
    </div>
  )
}

/** Ce scrie langa XDR dupa ce s-a ales un mod pentru selectie. */
const XDR_SHORT: Record<XdrMode, string> = { follow: 'Follow', on: 'Always on', off: 'Always off' }

/*
 * `AgentSettings::saveModuleMenu`, ramura 'enabled_application_icon': scrie
 * `HideIcon` = 0 si bitul 8 din `Menus`. Fara iconita, meniul agentului n-are
 * de unde sa se deschida pe masina.
 */
const APP_ICON_HELP =
  'Shows the agent’s icon on the selected machines. The Application Menu column lists modules in the menu that opens from this icon, so without the icon that menu cannot be opened.'
